/**
 * Cart Product Component Controller
 * Manages wishlist integration
 */

const BoboCartProduct = (() => {
  const CONFIG = {
    WISHLIST_CHECK_INTERVAL: 200,
    WISHLIST_MAX_ATTEMPTS: 15
  };

  const SELECTORS = {
    productItem: '.custom-cart-product-item',
    wishlistButton: '.wk-button',
    wishlistLabel: '.wk-button__label',
    cardNoteImage: '[data-card-note-image]'
  };

  const CLASSES = {
    wishlistAdd: 'wk-button--add',
    wishlistRemove: 'wk-button--remove',
    wishlistLoaded: 'wk-button-loaded'
  };

  /**
   * Waits for WishlistKing SDK to be available
   * @param {Function} callback - Function to execute when SDK is ready
   * @param {number} interval - Check interval in milliseconds
   * @param {number} maxAttempts - Maximum number of attempts
   */
  const waitForWishlistKing = (
    callback,
    interval = CONFIG.WISHLIST_CHECK_INTERVAL,
    maxAttempts = CONFIG.WISHLIST_MAX_ATTEMPTS
  ) => {
    let attempts = 0;

    const check = () => {
      if (window.WishlistKing?.toolkit) {
        callback();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(check, interval);
      }
    };

    check();
  };

  /**
   * Gets all wishlist buttons for a specific product ID
   * @param {string} productId - Product ID to search for
   * @returns {NodeList} All matching wishlist buttons
   */
  const getButtonsByProductId = (productId) => {
    return document.querySelectorAll(
      `${SELECTORS.wishlistButton}[data-product-id="${productId}"]`
    );
  };

  /**
   * Updates wishlist button state
   * @param {HTMLElement} button - Wishlist button element
   * @param {boolean} isInWishlist - Whether product is in wishlist
   */
  const updateWishlistButtonState = (button, isInWishlist) => {    
    const label = button.querySelector(SELECTORS.wishlistLabel);
    
    if (label && isInWishlist) {
      label.textContent = savedInWishlistText;
    } else if (label) {
      label.textContent = notSavedInWishlistText;
    }

    button.classList.toggle(CLASSES.wishlistAdd, !isInWishlist);
    button.classList.toggle(CLASSES.wishlistRemove, isInWishlist);
  };

  /**
   * Updates all buttons with the same product ID
   * @param {string} productId - Product ID to update
   * @param {boolean} isInWishlist - Whether product is in wishlist
   */
  const updateAllProductButtons = (productId, isInWishlist) => {    
    const buttons = getButtonsByProductId(productId);
    
    buttons.forEach((button) => {
      updateWishlistButtonState(button, isInWishlist);
    });
  };

  /**
   * Initializes wishlist buttons state based on current wishlist
   */
  const initWishlistButtons = () => {    
    const buttons = document.querySelectorAll(SELECTORS.wishlistButton);

    buttons.forEach((button) => {
      const productId = button.dataset.productId;

      if (productId && window.WishlistKing.toolkit.getProduct(productId)) {
        updateWishlistButtonState(button, true);
      }

      button.classList.add(CLASSES.wishlistLoaded);
    });
  };

  /**
   * Handles wishlist button click
   * @param {Event} event - Click event
   */
  const handleWishlistClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;

    if (!productId) { return; }

    const isAdding = button.classList.contains(CLASSES.wishlistAdd);

    const action = isAdding
      ? window.WishlistKing.toolkit.addProduct(productId, variantId)
      : window.WishlistKing.toolkit.removeProduct(productId);

    action
      .then(() => {
        // Update all buttons with same product ID
        updateAllProductButtons(productId, isAdding);
      })
      .catch((error) => {
      });
  };

  /**
   * Initializes wishlist button event listeners
   */
  const initWishlistEventListeners = () => {
    const productItems = document.querySelectorAll(SELECTORS.productItem);

    productItems.forEach((item) => {
      const button = item.querySelector(SELECTORS.wishlistButton);

      if (button && !button.dataset.listenerAttached) {
        button.addEventListener('click', handleWishlistClick);
        button.dataset.listenerAttached = 'true';
      }
    });
  };

  /**
   * Applies the card note image exposed by the "Cart note message" block.
   * The mini-cart renders in an isolated section context, so the image cannot be
   * resolved in Liquid and is provided through window.cardNoteImage instead.
   */
  const applyCardNoteImage = () => {
    if (!window.cardNoteImage?.url) return;

    document.querySelectorAll(SELECTORS.cardNoteImage).forEach((image) => {
      if (image.getAttribute('src') === window.cardNoteImage.url) return;

      image.setAttribute('src', window.cardNoteImage.url);
      image.setAttribute('alt', window.cardNoteImage.alt || '');

      if (window.cardNoteImage.height) {
        image.setAttribute('height', window.cardNoteImage.height);
      }
    });
  };

  /**
   * Initializes all cart product components
   */
  const init = () => {    
    applyCardNoteImage();

    waitForWishlistKing(() => {
      initWishlistButtons();
      initWishlistEventListeners();
    });
  };

  /**
   * Refreshes components after cart update
   * Used when cart is dynamically updated (e.g., mini-cart refresh)
   */
  const refresh = () => {  
    applyCardNoteImage();

    if (window.WishlistKing?.toolkit) {
      initWishlistButtons();
      initWishlistEventListeners();
    }
  };

  return {
    init,
    refresh
  };
})();

// Auto-initialize on DOM ready
if (document.readyState === 'loading') {  
  document.addEventListener('DOMContentLoaded', () => {
    BoboCartProduct.init();
  });
} else {  
  BoboCartProduct.init();
}

// Expose globally for manual refresh after cart updates
window.BoboCartProduct = BoboCartProduct;